/**
 * Data grid. Screen spec 4.10 and S11.
 *
 * M1 shipped the spine: the sticky header, the keyboard map for moving
 * around, the selection ring, and staged editing with the pending changes
 * bar. M3 extends that spine rather than replacing it, and everything added
 * here is optional, so the kit gallery's four line call site still renders
 * exactly what it rendered before.
 *
 * What M3 adds, all of it from S11:
 *   - a row selector column, and Space toggles it from any cell in the row;
 *   - Tab and Shift Tab as cell movement, which is the spreadsheet contract;
 *   - Command or Control with Enter to send everything pending;
 *   - controlled staging, so the screen that owns the diff sheet owns the
 *     edits it is about to describe;
 *   - sortable headers, where the screen does the sorting and the grid only
 *     reports the intent, because a grid that sorted its own copy of the rows
 *     would disagree with the footer counting them;
 *   - zebra, the bleed variant, the row cap footer, and a zero row state.
 *
 * Nothing a person types touches the data until they press Save, and the
 * count of what is waiting is on screen the entire time. That safety rail is
 * the reason this screen can be handed to a first time builder at all.
 *
 * Every cell is a literal that came out of a database, so every cell is mono.
 * Numbers are right aligned and tabular, which is the machine spine every
 * dense surface in this product carries.
 */

import type { ReactNode } from 'react';
import { useRef, useState } from 'react';
import { Button } from '../primitives/Button';
import { Checkbox } from '../primitives/Checkbox';
import { t } from '../../lib/i18n/t';
import type { DbCell, DbColumn, DbRow } from '../../lib/types';
import '../../styles/components/grid.css';

export type Edits = Record<string, Record<string, DbCell>>;

export interface GridSort {
  column: string;
  direction: 'asc' | 'desc';
}

export interface DataGridProps {
  columns: DbColumn[];
  rows: DbRow[];
  /** Names the grid for a screen reader: the table's own name. */
  label: string;
  /** Called with the staged edits when a person presses Save. */
  onSave?: (edits: Edits) => void;
  /**
   * Present when a diff sheet stands between the edits and the data. The
   * pending bar's action then reads "Review and save" and hands over rather
   * than writing, which is S11's contract.
   */
  onReview?: () => void;
  /** Controlled staging. Omit it and the grid keeps its own. */
  edits?: Edits;
  onEditsChange?: (edits: Edits) => void;
  /**
   * Discard throws away more than the cell edits when the screen is also
   * holding rows that were added and not yet saved, so the screen gets told
   * rather than left with orphans it thinks are still pending.
   */
  onDiscard?: () => void;
  /** A query result and a managed table are both read only. */
  readOnly?: boolean;
  /** Column types beside the names. Advanced only on S11. */
  showTypes?: boolean;
  /** Shading on alternate rows. An Advanced toggle, off by default. */
  zebra?: boolean;
  /** Square corners and a sunken floor, for a grid that owns its screen. */
  bleed?: boolean;
  /** The row selector column. */
  selectable?: boolean;
  selected?: string[];
  onSelectedChange?: (ids: string[]) => void;
  /** Rows added in this session, marked as pending until they are saved. */
  newRowIds?: string[];
  sort?: GridSort | null;
  onSortChange?: (sort: GridSort | null) => void;
  /** The pagination bar. It sits inside the grid's own border. */
  footer?: ReactNode;
  /** What to render instead of rows when there are none. */
  empty?: ReactNode;
  /** False when the screen's one ink block belongs to something else. */
  ink?: boolean;
}

function display(value: DbCell): string {
  if (value === null) return 'null';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  return String(value);
}

/**
 * A typed cell out of what a person typed. An empty string in a column that
 * accepts nothing becomes null rather than an empty string, because those are
 * different facts and a grid that quietly turned one into the other would be
 * writing something nobody asked for.
 */
function parse(draft: string, column: DbColumn): DbCell {
  const trimmed = draft.trim();
  if (column.nullable && (trimmed === '' || trimmed.toLowerCase() === 'null')) return null;
  if (column.type === 'number') {
    const value = Number(trimmed);
    return trimmed !== '' && !Number.isNaN(value) ? value : draft;
  }
  if (column.type === 'boolean') return trimmed.toLowerCase() === 'true';
  return draft;
}

export function countEdits(edits: Edits): number {
  return Object.values(edits).reduce((sum, row) => sum + Object.keys(row).length, 0);
}

export function DataGrid({
  columns,
  rows,
  label,
  onSave,
  onReview,
  edits,
  onEditsChange,
  onDiscard,
  readOnly = false,
  showTypes = true,
  zebra = false,
  bleed = false,
  selectable = false,
  selected,
  onSelectedChange,
  newRowIds,
  sort = null,
  onSortChange,
  footer,
  empty,
  ink = true,
}: DataGridProps) {
  const [cell, setCell] = useState<{ row: number; col: number }>({ row: 0, col: 0 });
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [ownEdits, setOwnEdits] = useState<Edits>({});
  const bodyRef = useRef<HTMLTableSectionElement | null>(null);

  const staged = edits ?? ownEdits;
  const picked = selected ?? [];
  const added = newRowIds ?? [];
  const pending = countEdits(staged) + added.length;

  function setStaged(next: Edits) {
    if (onEditsChange) onEditsChange(next);
    else setOwnEdits(next);
  }

  function focusCell(row: number, col: number) {
    if (rows.length === 0) return;
    const next = {
      row: Math.min(rows.length - 1, Math.max(0, row)),
      col: Math.min(columns.length - 1, Math.max(0, col)),
    };
    setCell(next);
    const node = bodyRef.current?.querySelector<HTMLElement>(
      `[data-row="${next.row}"][data-col="${next.col}"]`,
    );
    node?.focus();
  }

  function valueAt(rowIndex: number, columnName: string): DbCell {
    const row = rows[rowIndex];
    const held = staged[row.id]?.[columnName];
    return held !== undefined ? held : row.cells[columnName];
  }

  function isStaged(rowIndex: number, columnName: string): boolean {
    return staged[rows[rowIndex].id]?.[columnName] !== undefined;
  }

  function beginEdit(rowIndex: number, columnName: string) {
    if (readOnly) return;
    setDraft(display(valueAt(rowIndex, columnName)));
    setEditing(true);
  }

  function commitEdit(rowIndex: number, column: DbColumn) {
    const row = rows[rowIndex];
    const original = display(row.cells[column.name]);
    setEditing(false);

    const next: Edits = { ...staged, [row.id]: { ...(staged[row.id] ?? {}) } };
    if (draft === original) delete next[row.id][column.name];
    else next[row.id][column.name] = parse(draft, column);
    if (Object.keys(next[row.id]).length === 0) delete next[row.id];
    setStaged(next);
  }

  function toggleRow(rowIndex: number) {
    if (!selectable || !onSelectedChange) return;
    const id = rows[rowIndex].id;
    onSelectedChange(picked.includes(id) ? picked.filter((one) => one !== id) : [...picked, id]);
  }

  function send() {
    if (pending === 0) return;
    if (onReview) {
      onReview();
      return;
    }
    onSave?.(staged);
    setStaged({});
  }

  function onKeyDown(event: React.KeyboardEvent<HTMLTableSectionElement>) {
    if (editing) {
      if (event.key === 'Escape') {
        event.preventDefault();
        setEditing(false);
        focusCell(cell.row, cell.col);
      }
      if (event.key === 'Enter') {
        event.preventDefault();
        commitEdit(cell.row, columns[cell.col]);
        focusCell(cell.row, cell.col);
      }
      return;
    }

    if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
      event.preventDefault();
      send();
      return;
    }

    const map: Record<string, [number, number]> = {
      ArrowUp: [-1, 0],
      ArrowDown: [1, 0],
      ArrowLeft: [0, -1],
      ArrowRight: [0, 1],
    };
    const move = map[event.key];
    if (move) {
      event.preventDefault();
      focusCell(cell.row + move[0], cell.col + move[1]);
      return;
    }

    // Tab is cell movement inside a grid, and it wraps to the next row, which
    // is the contract every spreadsheet a person has ever used already taught
    // them. It leaves the grid at the last cell rather than trapping.
    if (event.key === 'Tab') {
      const forward = !event.shiftKey;
      const atEdge = forward
        ? cell.row === rows.length - 1 && cell.col === columns.length - 1
        : cell.row === 0 && cell.col === 0;
      if (atEdge) return;
      event.preventDefault();
      if (forward && cell.col === columns.length - 1) focusCell(cell.row + 1, 0);
      else if (!forward && cell.col === 0) focusCell(cell.row - 1, columns.length - 1);
      else focusCell(cell.row, cell.col + (forward ? 1 : -1));
      return;
    }

    if (event.key === ' ') {
      if (!selectable) return;
      event.preventDefault();
      toggleRow(cell.row);
      return;
    }

    if (event.key === 'Home') {
      event.preventDefault();
      focusCell(cell.row, 0);
    } else if (event.key === 'End') {
      event.preventDefault();
      focusCell(cell.row, columns.length - 1);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      beginEdit(cell.row, columns[cell.col].name);
    }
  }

  function headerSort(column: DbColumn) {
    if (!onSortChange) return;
    if (sort?.column !== column.name) {
      onSortChange({ column: column.name, direction: 'asc' });
      return;
    }
    onSortChange(sort.direction === 'asc' ? { column: column.name, direction: 'desc' } : null);
  }

  const allPicked = selectable && rows.length > 0 && rows.every((row) => picked.includes(row.id));

  return (
    <div
      className="grid"
      data-zebra={zebra ? 'yes' : 'no'}
      data-bleed={bleed ? 'yes' : 'no'}
      data-read-only={readOnly ? 'yes' : 'no'}
    >
      <div className="grid__scroll">
        <table className="grid__el" role="grid" aria-label={label}>
          <thead className="grid__head">
            <tr>
              {selectable ? (
                <th scope="col" className="grid__th grid__th--pick">
                  <span className="grid__pick">
                    <Checkbox
                      label={t('Select every row on this page')}
                      checked={allPicked}
                      onChange={() =>
                        onSelectedChange?.(allPicked ? [] : rows.map((row) => row.id))
                      }
                    />
                  </span>
                </th>
              ) : null}

              {columns.map((column) => {
                const sorted =
                  sort?.column === column.name
                    ? sort.direction === 'asc'
                      ? 'ascending'
                      : 'descending'
                    : undefined;
                return (
                  <th
                    key={column.name}
                    scope="col"
                    className="grid__th"
                    data-type={column.type}
                    aria-sort={onSortChange ? (sorted ?? 'none') : undefined}
                  >
                    {onSortChange ? (
                      <button
                        type="button"
                        className="grid__sort"
                        onClick={() => headerSort(column)}
                      >
                        <span className="grid__col-name mono">{column.name}</span>
                        {showTypes ? <span className="grid__col-type">{column.type}</span> : null}
                      </button>
                    ) : (
                      <>
                        <span className="grid__col-name mono">{column.name}</span>
                        {showTypes ? <span className="grid__col-type">{column.type}</span> : null}
                      </>
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody ref={bodyRef} onKeyDown={onKeyDown}>
            {rows.map((row, rowIndex) => (
              <tr
                className="grid__row"
                key={row.id}
                data-focused={cell.row === rowIndex ? 'yes' : 'no'}
                data-picked={picked.includes(row.id) ? 'yes' : 'no'}
                data-new={added.includes(row.id) ? 'yes' : 'no'}
              >
                {selectable ? (
                  <td className="grid__td grid__td--pick">
                    <span className="grid__pick">
                      <Checkbox
                        label={t('Select this row')}
                        checked={picked.includes(row.id)}
                        onChange={() => toggleRow(rowIndex)}
                      />
                    </span>
                  </td>
                ) : null}

                {columns.map((column, colIndex) => {
                  const selectedCell = cell.row === rowIndex && cell.col === colIndex;
                  const value = valueAt(rowIndex, column.name);
                  return (
                    <td
                      key={column.name}
                      className="grid__td"
                      data-row={rowIndex}
                      data-col={colIndex}
                      data-type={column.type}
                      data-null={value === null ? 'yes' : 'no'}
                      data-staged={isStaged(rowIndex, column.name) ? 'yes' : 'no'}
                      data-selected={selectedCell ? 'yes' : 'no'}
                      tabIndex={selectedCell ? 0 : -1}
                      onClick={() => focusCell(rowIndex, colIndex)}
                      onDoubleClick={() => beginEdit(rowIndex, column.name)}
                    >
                      {selectedCell && editing ? (
                        <input
                          className="grid__input mono"
                          value={draft}
                          autoFocus
                          onChange={(event) => setDraft(event.target.value)}
                          onBlur={() => commitEdit(rowIndex, column)}
                        />
                      ) : (
                        <span className="grid__value mono">{display(value)}</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>

        {rows.length === 0 && empty ? <div className="grid__empty">{empty}</div> : null}
      </div>

      {footer ? <div className="grid__foot">{footer}</div> : null}

      {pending > 0 ? (
        <div className="grid__pending" role="status" aria-live="polite">
          {/* Separate keys rather than one with a number in it. There is no
              plural machinery in t(), and "1 changes" is the kind of sentence
              that makes a person doubt the rest of the screen. */}
          <span className="grid__pending-say">
            {pending === 1
              ? t('1 change not saved yet')
              : t('{n} changes not saved yet', { n: pending })}
          </span>
          <Button
            variant="ghost"
            onClick={() => {
              setStaged({});
              setEditing(false);
              onDiscard?.();
            }}
          >
            {t('Discard')}
          </Button>
          <Button variant={ink ? 'solid' : 'outline'} onClick={send}>
            {onReview ? t('Review and save') : t('Save changes')}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
